import { useEffect, useRef, useState } from "react";
import { Building2, Camera, Loader2, Plus, Save, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AdminOrgTheme from "@/pages/admin/AdminOrgTheme";
import AdminOrgPaymentProvider from "@/pages/admin/AdminOrgPaymentProvider";

function emptyForm() {
  return {
    name: "",
    slug: "",
    contact_name: "",
    contact_email: "",
    phone: "",
    kvk: "",
    iban: "",
    vat_number: "",
    notes: "",
    address: "",
    postal_code: "",
    city: "",
    logo_url: "",
    active: true,
  };
}

function OrgFields({ form, setForm, idPrefix }) {
  const F = (name, label, opts = {}) => (
    <label className={`block ${opts.className || ""}`}>
      <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
        {label} {opts.required && <span className="text-svj-pink">*</span>}
      </span>
      <input
        type={opts.type || "text"}
        value={form[name] || ""}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        data-testid={`${idPrefix}-${name}`}
        className="mt-1 w-full h-10 px-3 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
      />
    </label>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {F("name", "Naam", { required: true, className: "sm:col-span-2" })}
      {F("slug", "Slug (URL)")}
      {F("contact_name", "Contactpersoon")}
      {F("contact_email", "Contact e-mail", { type: "email" })}
      {F("phone", "Telefoon", { type: "tel" })}
      {F("address", "Straat + nr", { className: "sm:col-span-2" })}
      {F("postal_code", "Postcode")}
      {F("city", "Plaats")}
      {F("kvk", "KvK")}
      {F("iban", "IBAN")}
      {F("vat_number", "Btw-nummer")}
      <label className="block sm:col-span-2 lg:col-span-3">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Notities</span>
        <textarea
          value={form.notes || ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          data-testid={`${idPrefix}-notes`}
          className="mt-1 w-full px-3 py-2 rounded-lg bg-svj-bg border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan resize-y"
        />
      </label>
    </div>
  );
}

function LogoUploader({ orgId, currentUrl, onUploaded, authAxios }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Bestand te groot (max 5MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Do NOT set Content-Type manually — the browser must add the multipart
      // boundary itself, which it skips once the header object already has the key.
      const { data } = await authAxios.post(`/admin/organizations/${orgId}/logo`, fd);
      onUploaded(data.logo_url);
      toast.success("Logo geüpload");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-4">
      <div className="h-16 w-16 rounded-full bg-white border border-svj-border overflow-hidden flex items-center justify-center shrink-0">
        {currentUrl ? (
          <img src={currentUrl} alt="logo" className="w-full h-full object-cover" />
        ) : (
          <Building2 className="h-7 w-7 text-svj-purple" />
        )}
      </div>
      <div>
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onFile} className="hidden" data-testid={`org-logo-input-${orgId}`} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`org-logo-upload-${orgId}`}
          className="inline-flex items-center gap-2 rounded-full border-2 border-svj-cyan text-svj-cyan font-bold py-2 px-4 text-sm hover:bg-svj-cyan/10 transition-colors"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
          {currentUrl ? "Vervang logo" : "Upload logo"}
        </button>
        <div className="mt-1 text-[10px] text-slate-500">PNG/JPG/WEBP · max 5MB · vierkant werkt het best</div>
      </div>
    </div>
  );
}

export default function AdminOrganizations() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get("/admin/organizations");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!createForm.name.trim()) return toast.error("Naam is verplicht");
    setCreating(true);
    try {
      await authAxios.post("/admin/organizations", createForm);
      toast.success("Organisatie aangemaakt");
      setCreateForm(emptyForm());
      setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (o) => {
    setExpandedId(o.id);
    setEditForm({ ...emptyForm(), ...o });
  };

  const saveEdit = async (orgId) => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      delete payload.id;
      delete payload.tickets_enabled;
      await authAxios.patch(`/admin/organizations/${orgId}`, payload);
      toast.success("Organisatie opgeslagen");
      setExpandedId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async (o) => {
    if (!window.confirm(`"${o.name}" verwijderen? Als er events of gebruikers aan hangen wordt de organisatie alleen uit dit ticketplatform ontkoppeld.`)) return;
    try {
      const { data } = await authAxios.delete(`/admin/organizations/${o.id}`);
      toast.success(data.unlinked ? "Ontkoppeld (er hingen events of gebruikers aan)" : "Verwijderd");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const filtered = items.filter((o) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [o.name, o.city, o.contact_email, o.slug, o.kvk].filter(Boolean).join(" ").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-orgs-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Organisaties</h1>
          <p className="mt-1 text-sm text-slate-400">
            Superadmin — beheer alle organisaties met tickets-module actief.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          data-testid="admin-orgs-new"
          className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold py-2.5 px-5 text-sm hover:shadow-[0_0_24px_rgba(214,61,122,0.4)] transition-all"
        >
          <Plus className="h-4 w-4" /> Nieuwe organisatie
        </button>
      </div>

      <div className="mt-6 relative max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op naam, plaats, KvK…"
          data-testid="admin-orgs-search"
          className="w-full h-11 pl-11 pr-4 rounded-full bg-svj-card border border-svj-border text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-svj-cyan focus:border-transparent text-base"
        />
      </div>

      {showCreate && (
        <div className="mt-6 rounded-2xl border border-svj-border bg-svj-card p-5" data-testid="admin-orgs-create-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-white uppercase tracking-wider">Nieuwe organisatie</div>
            <button type="button" onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4">
            <OrgFields form={createForm} setForm={setCreateForm} idPrefix="new-org" />
            <div className="mt-1 text-[10px] text-slate-500">Logo kun je uploaden zodra de organisatie is opgeslagen.</div>
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowCreate(false)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2">Annuleren</button>
            <button type="button" onClick={create} disabled={creating} data-testid="new-org-submit" className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Aanmaken
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-svj-border overflow-hidden">
        {loading && <div className="p-8 text-sm text-slate-400">Laden…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center" data-testid="admin-orgs-empty">
            <Building2 className="h-8 w-8 text-svj-cyan mx-auto" />
            <div className="mt-3 text-white font-semibold text-sm">Nog geen organisaties</div>
          </div>
        )}
        {!loading && filtered.map((o) => {
          const isOpen = expandedId === o.id;
          return (
            <div key={o.id} className="bg-svj-card border-b border-svj-border last:border-b-0" data-testid={`admin-org-row-${o.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-4 items-center">
                <div className="md:col-span-5 flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-white border border-svj-border overflow-hidden flex items-center justify-center shrink-0">
                    {o.logo_url ? (
                      <img src={o.logo_url} alt={o.name} className="w-full h-full object-cover" />
                    ) : (
                      <Building2 className="h-5 w-5 text-svj-purple" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{o.name}</div>
                    <div className="text-xs text-slate-400 truncate">
                      {o.city || "—"}
                      {o.contact_email ? ` · ${o.contact_email}` : ""}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-3 text-xs text-slate-400 truncate">KvK: {o.kvk || "—"}</div>
                <div className="md:col-span-2">
                  <span className={`inline-flex h-2.5 w-2.5 rounded-full ${o.active ? "bg-emerald-400" : "bg-slate-500"}`} title={o.active ? "Actief" : "Inactief"} />
                  <span className="ml-2 text-xs text-slate-400">{o.active ? "Actief" : "Inactief"}</span>
                </div>
                <div className="md:col-span-2 flex md:justify-end gap-1.5">
                  <button type="button" onClick={() => isOpen ? setExpandedId(null) : startEdit(o)} data-testid={`org-edit-${o.id}`} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-xs font-semibold px-3 py-1.5">
                    {isOpen ? "Sluiten" : "Bewerken"}
                  </button>
                  <button type="button" onClick={() => del(o)} data-testid={`org-delete-${o.id}`} className="h-8 w-8 rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-red-400 hover:border-red-500/50 flex items-center justify-center">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="px-5 pb-6 bg-svj-bg/40 border-t border-svj-border" data-testid={`org-edit-panel-${o.id}`}>
                  <div className="pt-5">
                    <LogoUploader
                      orgId={o.id}
                      currentUrl={editForm.logo_url}
                      onUploaded={(url) => { setEditForm({ ...editForm, logo_url: url }); load(); }}
                      authAxios={authAxios}
                    />
                  </div>
                  <div className="mt-5">
                    <OrgFields form={editForm} setForm={setEditForm} idPrefix={`org-${o.id}`} />
                  </div>
                  <AdminOrgTheme org={o} canEdit onSaved={() => load()} />
                  <AdminOrgPaymentProvider org={o} />
                  <div className="mt-4 flex items-center gap-3 justify-between">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                      <input type="checkbox" checked={!!editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} className="h-4 w-4" />
                      Actief
                    </label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setExpandedId(null)} className="rounded-lg border bg-svj-bg border-svj-border text-slate-300 hover:text-white text-sm font-semibold px-4 py-2">Annuleren</button>
                      <button type="button" onClick={() => saveEdit(o.id)} disabled={saving} data-testid={`org-save-${o.id}`} className="rounded-full bg-gradient-to-r from-svj-orange via-svj-pink to-svj-purple text-white font-bold text-sm px-5 py-2 inline-flex items-center gap-2">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Opslaan
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
